import { useState } from 'react';
import PropTypes from 'prop-types';
import { BIBLE_BOOKS } from '../../utils/bibleBooks';
import { CUSTOM_KINDS, PLAN_CHOICES, planDraft } from './familyPlanView';
import './FamilyPlanEditor.css';

const bookList = (stored) => String(stored || 'Genesis')
  .split('|')
  .map((name) => name.trim())
  .filter(Boolean);

const FamilyPlanEditor = ({ plan, onSave, onClose }) => {
  const [mode, setMode] = useState(plan?.mode === 'pooled' ? 'pooled' : 'same');
  const [kind, setKind] = useState(plan?.customKind || 'chapters');
  const [chapters, setChapters] = useState(plan?.chapterTarget || 30);
  const [bibles, setBibles] = useState(plan?.bibleTarget || 1);
  const [books, setBooks] = useState(bookList(plan?.bookNames));
  const [customOpen, setCustomOpen] = useState(false);

  const draft = (goal, nextMode = mode) => planDraft({
    goal,
    mode: nextMode,
    customKind: kind,
    chapterTarget: chapters,
    bibleTarget: bibles,
    bookNames: books.join('|')
  });

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    if (plan?.goal) onSave(draft(plan.goal, nextMode), false);
  };

  const toggleBook = (name) => {
    setBooks((current) => (
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    ));
  };

  return (
    <section className="family-editor" aria-label="Family plan">
      <div className="family-editor-bar">
        <strong>{customOpen ? 'Custom' : 'Family plan'}</strong>
        <button type="button" className="family-text-button" onClick={customOpen ? () => setCustomOpen(false) : onClose}>
          {customOpen ? 'Back' : 'Done'}
        </button>
      </div>

      {!customOpen && (
        <>
          <div className="family-pills" role="group" aria-label="Plan mode">
            <button type="button" className={mode === 'same' ? 'is-on' : ''} onClick={() => chooseMode('same')}>
              Same plan
            </button>
            <button type="button" className={mode === 'pooled' ? 'is-on' : ''} onClick={() => chooseMode('pooled')}>
              Pooling
            </button>
          </div>
          <p className="family-editor-note">
            Same plan means each person finishes it. Pooling means the family finishes one plan together. A missed day is added to the next, through Reformation Day.
          </p>
          <ul className="family-editor-goals">
            {PLAN_CHOICES.filter((goal) => goal.key !== 'custom').map((goal) => (
              <li key={goal.key}>
                <button type="button" onClick={() => onSave(draft(goal.key), true)}>
                  <span>{goal.title}</span>
                  {plan?.goal === goal.key && <small>Current</small>}
                </button>
              </li>
            ))}
            <li>
              <button type="button" onClick={() => setCustomOpen(true)}>
                <span>Custom</span>
                {plan?.goal === 'custom' && <small>Current</small>}
              </button>
            </li>
          </ul>
        </>
      )}

      {customOpen && (
        <>
          <div className="family-pills" role="group" aria-label="Custom plan">
            {CUSTOM_KINDS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={option.key === kind ? 'is-on' : ''}
                onClick={() => setKind(option.key)}
              >
                {option.title}
              </button>
            ))}
          </div>
          {kind === 'books' ? (
            <ul className="family-editor-books">
              {BIBLE_BOOKS.map((book) => (
                <li key={book.name}>
                  <button type="button" onClick={() => toggleBook(book.name)}>
                    <span>{book.name}</span>
                    <small>{books.includes(book.name) ? 'Selected' : book.chapters}</small>
                  </button>
                </li>
              ))}
            </ul>
          ) : kind === 'chapters' ? (
            <label className="family-editor-amount">
              <span>{Math.min(1189, Math.max(1, Number(chapters) || 1)).toLocaleString()} chapters</span>
              <input
                type="range"
                min="1"
                max="1189"
                value={chapters}
                onChange={(event) => setChapters(Number(event.target.value))}
              />
            </label>
          ) : (
            <label className="family-editor-amount">
              <span>
                {Math.min(10, Math.max(1, Number(bibles) || 1))}
                {' '}
                {Math.min(10, Math.max(1, Number(bibles) || 1)) === 1 ? 'Bible' : 'Bibles'}
              </span>
              <input
                type="range"
                min="1"
                max="10"
                value={bibles}
                onChange={(event) => setBibles(Number(event.target.value))}
              />
            </label>
          )}
          <button type="button" className="family-use" onClick={() => onSave(draft('custom'), true)}>
            Use this plan
          </button>
        </>
      )}
    </section>
  );
};

FamilyPlanEditor.propTypes = {
  plan: PropTypes.shape({
    goal: PropTypes.string,
    mode: PropTypes.string,
    customKind: PropTypes.string,
    chapterTarget: PropTypes.number,
    bibleTarget: PropTypes.number,
    bookNames: PropTypes.string
  }),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

FamilyPlanEditor.defaultProps = {
  plan: null
};

export default FamilyPlanEditor;
